import { CloseIcon } from '@chakra-ui/icons';
import { Box, HStack, IconButton, Portal } from '@chakra-ui/react';
import { useCallback, useEffect, useRef, useState } from 'react';

import Text from 'components/Text';

import { ITourStep } from './tourConfig';
import useSandboxTheme from '../useSandboxTheme';

interface ITargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface ITourSpotlightProps {
  step: ITourStep;
  stepIndex: number;
  totalSteps: number;
  onNext: () => void;
  onPrev: () => void;
  onSkip: () => void;
  isMerlinOpen?: boolean;
  isAutoStarted?: boolean;
}

const SPOTLIGHT_PADDING = 6;
const TOOLTIP_WIDTH = 300;
const TOOLTIP_OFFSET = 14;
const ARROW_SIZE = 8;
/** Duration (ms) of the fade-out / fade-in half-cycle */
const FADE_MS = 200;

/**
 * Compute tooltip position. For `top` placement we return the final
 * rendered top (i.e. above the target) rather than relying on translateY(-100%),
 * because CSS transforms interact badly with viewport clamping.
 * We estimate tooltip height as 200px (safe upper bound).
 */
const TOOLTIP_HEIGHT_ESTIMATE = 200;

const getTooltipPosition = (
  rect: ITargetRect,
  placement: ITourStep['placement']
): { top: number; left: number } => {
  switch (placement) {
    case 'top':
      return {
        top: rect.top - TOOLTIP_OFFSET - TOOLTIP_HEIGHT_ESTIMATE,
        left: rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2,
      };
    case 'bottom':
      return {
        top: rect.top + rect.height + TOOLTIP_OFFSET,
        left: rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2,
      };
    case 'left':
      return {
        top: rect.top + rect.height / 2 - 60,
        left: rect.left - TOOLTIP_WIDTH - TOOLTIP_OFFSET,
      };
    case 'right':
      return {
        top: rect.top + rect.height / 2 - 60,
        left: rect.left + rect.width + TOOLTIP_OFFSET,
      };
  }
};

const clampToViewport = (pos: { top: number; left: number }): { top: number; left: number } => {
  return {
    top: Math.max(8, Math.min(pos.top, window.innerHeight - TOOLTIP_HEIGHT_ESTIMATE - 8)),
    left: Math.max(8, Math.min(pos.left, window.innerWidth - TOOLTIP_WIDTH - 8)),
  };
};

/**
 * Compute arrow left offset so it points at the target center,
 * accounting for viewport clamping that may shift the tooltip.
 */
const getArrowLeftPx = (
  targetRect: ITargetRect,
  tooltipLeft: number,
): string => {
  const targetCenter = targetRect.left + targetRect.width / 2;
  const offset = targetCenter - tooltipLeft;
  const clamped = Math.max(16, Math.min(offset, TOOLTIP_WIDTH - 16));
  return `${clamped}px`;
};

/** CSS for the arrow that connects tooltip to target */
const getArrowStyles = (
  placement: ITourStep['placement'],
  isLight: boolean,
  targetRect: ITargetRect,
  tooltipLeft: number,
): Record<string, unknown> => {
  const bg = isLight ? 'white' : '#1A202C';
  const borderColor = isLight ? '#E2E8F0' : 'rgba(255,255,255,0.16)';
  const size = `${ARROW_SIZE}px`;

  const base = {
    content: '""',
    position: 'absolute' as const,
    width: 0,
    height: 0,
  };

  const arrowLeft = getArrowLeftPx(targetRect, tooltipLeft);
  const arrowTransform = 'translateX(-50%)';

  switch (placement) {
    case 'bottom':
      return {
        _before: {
          ...base,
          top: `-${ARROW_SIZE + 1}px`,
          left: arrowLeft,
          transform: arrowTransform,
          borderLeft: `${size} solid transparent`,
          borderRight: `${size} solid transparent`,
          borderBottom: `${size} solid ${borderColor}`,
        },
        _after: {
          ...base,
          top: `-${ARROW_SIZE}px`,
          left: arrowLeft,
          transform: arrowTransform,
          borderLeft: `${size} solid transparent`,
          borderRight: `${size} solid transparent`,
          borderBottom: `${size} solid ${bg}`,
        },
      };
    case 'top':
      return {
        _before: {
          ...base,
          bottom: `-${ARROW_SIZE + 1}px`,
          left: arrowLeft,
          transform: arrowTransform,
          borderLeft: `${size} solid transparent`,
          borderRight: `${size} solid transparent`,
          borderTop: `${size} solid ${borderColor}`,
        },
        _after: {
          ...base,
          bottom: `-${ARROW_SIZE}px`,
          left: arrowLeft,
          transform: arrowTransform,
          borderLeft: `${size} solid transparent`,
          borderRight: `${size} solid transparent`,
          borderTop: `${size} solid ${bg}`,
        },
      };
    case 'left':
      return {
        _before: {
          ...base,
          top: '20px',
          right: `-${ARROW_SIZE + 1}px`,
          borderTop: `${size} solid transparent`,
          borderBottom: `${size} solid transparent`,
          borderLeft: `${size} solid ${borderColor}`,
        },
        _after: {
          ...base,
          top: '20px',
          right: `-${ARROW_SIZE}px`,
          borderTop: `${size} solid transparent`,
          borderBottom: `${size} solid transparent`,
          borderLeft: `${size} solid ${bg}`,
        },
      };
    case 'right':
      return {
        _before: {
          ...base,
          top: '20px',
          left: `-${ARROW_SIZE + 1}px`,
          borderTop: `${size} solid transparent`,
          borderBottom: `${size} solid transparent`,
          borderRight: `${size} solid ${borderColor}`,
        },
        _after: {
          ...base,
          top: '20px',
          left: `-${ARROW_SIZE}px`,
          borderTop: `${size} solid transparent`,
          borderBottom: `${size} solid transparent`,
          borderRight: `${size} solid ${bg}`,
        },
      };
  }
};

const AUTOSTART_GRACE_MS = 2500;

const TourSpotlight = ({
  step,
  stepIndex,
  totalSteps,
  onNext,
  onPrev,
  onSkip,
  isMerlinOpen = false,
  isAutoStarted = false,
}: ITourSpotlightProps) => {
  /**
   * We keep two rects:
   * - `renderRect`: the position currently being displayed (only updated while hidden)
   * - `pendingRect`: the next position to show after fade-out completes
   * This prevents blinking — we never move the visible spotlight/tooltip.
   */
  const [renderRect, setRenderRect] = useState<ITargetRect | null>(null);
  const [renderStep, setRenderStep] = useState(step);
  const [visible, setVisible] = useState(false);
  const [graceActive, setGraceActive] = useState(isAutoStarted);
  const transitioningRef = useRef(false);
  const skipCalledRef = useRef(false);
  const mountedRef = useRef(true);
  const { isLight } = useSandboxTheme();

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  // Grace period: prevent accidental close on auto-started tours
  useEffect(() => {
    if (!isAutoStarted) return;
    setGraceActive(true);
    const t = setTimeout(() => {
      if (mountedRef.current) setGraceActive(false);
    }, AUTOSTART_GRACE_MS);
    return () => clearTimeout(t);
  }, [isAutoStarted]);

  const findTarget = useCallback((target: string) => {
    const el = document.querySelector(`[data-tour="${target}"]`);
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    return {
      top: rect.top - SPOTLIGHT_PADDING,
      left: rect.left - SPOTLIGHT_PADDING,
      width: rect.width + SPOTLIGHT_PADDING * 2,
      height: rect.height + SPOTLIGHT_PADDING * 2,
    };
  }, []);

  useEffect(() => {
    skipCalledRef.current = false;
    transitioningRef.current = true;

    const isFirstStep = !renderRect;

    if (isFirstStep) {
      // First appearance — find target, set rect, fade in
      const t = setTimeout(() => {
        if (!mountedRef.current) return;
        const rect = findTarget(step.target);
        if (rect) {
          setRenderRect(rect);
          setRenderStep(step);
          // Allow one frame for DOM to place elements, then fade in
          requestAnimationFrame(() => {
            if (!mountedRef.current) return;
            setVisible(true);
            transitioningRef.current = false;
          });
        } else if (!skipCalledRef.current) {
          skipCalledRef.current = true;
          transitioningRef.current = false;
          onNext();
        }
      }, 80);
      return () => clearTimeout(t);
    }

    // Subsequent steps: fade out → reposition (invisible) → fade in
    setVisible(false);

    // Wait for fade-out to complete, then reposition
    const t = setTimeout(() => {
      if (!mountedRef.current) return;
      const rect = findTarget(step.target);
      if (rect) {
        // Update position while invisible — no visual jump
        setRenderRect(rect);
        setRenderStep(step);
        // Wait two frames: one for React render, one for layout
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            if (!mountedRef.current) return;
            setVisible(true);
            transitioningRef.current = false;
          });
        });
      } else if (!skipCalledRef.current) {
        skipCalledRef.current = true;
        transitioningRef.current = false;
        onNext();
      }
    }, FADE_MS + 50); // FADE_MS for CSS transition + 50ms buffer

    return () => clearTimeout(t);
  }, [step, findTarget, onNext]); // eslint-disable-line react-hooks/exhaustive-deps

  // Guarded navigation — prevents rapid clicking/keyboard from breaking positions
  const guardedNext = useCallback(() => {
    if (transitioningRef.current) return;
    onNext();
  }, [onNext]);

  const guardedPrev = useCallback(() => {
    if (transitioningRef.current) return;
    onPrev();
  }, [onPrev]);

  const guardedSkip = useCallback(() => {
    if (transitioningRef.current || graceActive) return;
    onSkip();
  }, [onSkip, graceActive]);

  // Keyboard navigation: ← → Enter Escape
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (transitioningRef.current) return;
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        guardedNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (stepIndex > 0) guardedPrev();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        guardedSkip();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [guardedNext, guardedPrev, guardedSkip, stepIndex]);

  if (!renderRect) return null;

  const rawPos = getTooltipPosition(renderRect, renderStep.placement);
  const tooltipPos = clampToViewport(rawPos);
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === totalSteps - 1;

  const backdropColor = isLight ? 'rgba(0, 0, 0, 0.55)' : 'rgba(0, 0, 0, 0.7)';
  const accentGlow = isLight
    ? '0 0 0 2px rgba(159, 122, 234, 0.35), 0 0 24px 8px rgba(159, 122, 234, 0.12)'
    : '0 0 0 2px rgba(159, 122, 234, 0.4), 0 0 32px 10px rgba(159, 122, 234, 0.15)';

  const arrowStyles = getArrowStyles(renderStep.placement, isLight, renderRect, tooltipPos.left);

  const fadeDuration = `${FADE_MS}ms`;

  return (
    <Portal>
      {/* Clickable backdrop */}
      <Box
        position="fixed"
        inset={0}
        zIndex={10001}
        cursor={graceActive ? 'default' : 'pointer'}
        onClick={guardedSkip}
      />

      {/* Spotlight cutout with gentle pulse */}
      <Box
        position="fixed"
        top={`${renderRect.top}px`}
        left={`${renderRect.left}px`}
        width={`${renderRect.width}px`}
        height={`${renderRect.height}px`}
        borderRadius="6px"
        boxShadow={`0 0 0 9999px ${backdropColor}, ${accentGlow}`}
        zIndex={10001}
        pointerEvents="none"
        opacity={visible ? 1 : 0}
        transition={`opacity ${fadeDuration} ease-in-out`}
        sx={{
          '@keyframes tourSpotlightPulse': {
            '0%, 100%': { boxShadow: `0 0 0 9999px ${backdropColor}, ${accentGlow}` },
            '50%': {
              boxShadow: `0 0 0 9999px ${backdropColor}, ${
                isLight
                  ? '0 0 0 3px rgba(159, 122, 234, 0.45), 0 0 32px 12px rgba(159, 122, 234, 0.18)'
                  : '0 0 0 3px rgba(159, 122, 234, 0.55), 0 0 40px 14px rgba(159, 122, 234, 0.22)'
              }`,
            },
          },
          animation: visible ? 'tourSpotlightPulse 2.5s ease-in-out infinite' : 'none',
        }}
      />

      {/* Tooltip card with arrow */}
      <Box
        position="fixed"
        top={`${tooltipPos.top}px`}
        left={`${tooltipPos.left}px`}
        width={`${TOOLTIP_WIDTH}px`}
        zIndex={10002}
        bg={isLight ? 'white' : '#1A202C'}
        border="1px solid"
        borderColor={isLight ? 'gray.200' : 'whiteAlpha.200'}
        borderRadius="10px"
        boxShadow={isLight
          ? '0 12px 40px rgba(0,0,0,0.15), 0 4px 12px rgba(0,0,0,0.08)'
          : '0 12px 48px rgba(0,0,0,0.6), 0 4px 16px rgba(0,0,0,0.4)'
        }
        overflow="visible"
        opacity={visible ? 1 : 0}
        transition={`opacity ${fadeDuration} ease-in-out`}
        sx={arrowStyles}
      >
        <Box p="16px 18px 14px">
          {/* Header */}
          <HStack justifyContent="space-between" alignItems="center" mb="8px">
            <Text fontSize="medium" fontWeight="bold">
              {renderStep.title}
            </Text>
            <IconButton
              aria-label="Close tour"
              icon={<CloseIcon boxSize="8px" />}
              size="xs"
              variant="ghost"
              minW="24px"
              h="24px"
              borderRadius="6px"
              color={isLight ? 'gray.400' : 'whiteAlpha.500'}
              _hover={{ bg: isLight ? 'gray.100' : 'whiteAlpha.100', color: isLight ? 'gray.600' : 'whiteAlpha.800' }}
              onClick={guardedSkip}
              opacity={graceActive ? 0.3 : 1}
              cursor={graceActive ? 'default' : 'pointer'}
              transition="opacity 0.3s ease"
            />
          </HStack>

          {/* Description */}
          <Text
            fontSize="small"
            opacity={isLight ? 'secondary' : 'default'}
            color={isLight ? undefined : 'whiteAlpha.800'}
            fontWeight="regular"
            lineHeight="1.5"
            mb={isLast ? '10px' : '16px'}
          >
            {isMerlinOpen && renderStep.merlinDescription
              ? renderStep.merlinDescription
              : renderStep.description}
          </Text>

          {/* Last-step hint */}
          {isLast && (
            <Text
              fontSize="micro"
              opacity="tertiary"
              fontWeight="medium"
              lineHeight="1.4"
              mb="12px"
              fontStyle="italic"
            >
              You can always replay this tour from the Tour button.
            </Text>
          )}

          {/* Divider */}
          <Box
            height="1px"
            bg={isLight ? 'gray.100' : 'whiteAlpha.100'}
            mb="12px"
          />

          {/* Footer */}
          <HStack justifyContent="space-between" alignItems="center">
            <Box minW="44px">
              {!isFirst && (
                <Box
                  as="button"
                  onClick={guardedPrev}
                  cursor="pointer"
                  _hover={{ opacity: 1 }}
                  _focus={{ outline: 'none' }}
                >
                  <Text fontSize="micro" opacity="tertiary" fontWeight="medium">
                    Back
                  </Text>
                </Box>
              )}
            </Box>

            <HStack spacing="8px" alignItems="center">
              <Box
                as="span"
                style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.03em' }}
                color={isLight ? 'gray.400' : 'whiteAlpha.400'}
              >
                {stepIndex + 1}/{totalSteps}
              </Box>
              <Box
                as="span"
                style={{ fontSize: '10px', fontWeight: 500 }}
                color={isLight ? 'gray.300' : 'whiteAlpha.300'}
              >
                ← →
              </Box>
            </HStack>

            <Box
              as="button"
              onClick={guardedNext}
              cursor="pointer"
              minW="44px"
              textAlign="right"
              _hover={{ opacity: 0.8 }}
              _focus={{ outline: 'none' }}
            >
              <Text fontSize="small" color="purple.300" fontWeight="bold">
                {isLast ? 'Done' : 'Next'}
              </Text>
            </Box>
          </HStack>
        </Box>
      </Box>
    </Portal>
  );
};

export default TourSpotlight;
